import { toast } from 'react-toastify'
import { AutomationModal, AutomationFormData } from './AutomationModal'
import {
  AutomationCreatePayload,
  AutomationDetail,
  AutomationMultipartPayload,
} from 'models/automation'
import useApiService from 'services/api'
import { useEffect, useState } from 'react'
import {
  cleanAutomationActionPayload,
  cleanAutomationConditionPayload,
} from './utils'

interface CopyAutomationModalProps {
  show: boolean
  handleClose: () => void
  automationId: number
  onAutomationCreated?: () => void
}

export function CopyAutomationModal({
  show,
  handleClose,
  automationId,
  onAutomationCreated,
}: CopyAutomationModalProps) {
  const api = useApiService()
  const [automation, setAutomation] = useState<AutomationDetail | null>(null)
  const [isLoadingAutomation, setIsLoadingAutomation] = useState(false)

  useEffect(() => {
    if (show && automationId) {
      fetchAutomationData()
    }
  }, [show, automationId])

  const fetchAutomationData = async () => {
    try {
      setIsLoadingAutomation(true)
      const response = await api.getAutomationById(automationId)
      setAutomation(response)
    } catch (err) {
      console.error('Failed to fetch automation:', err)
      toast.error('Failed to load automation data')
      handleClose()
    } finally {
      setIsLoadingAutomation(false)
    }
  }

  const handleSubmitSuccess = () => {
    toast.success('Automation copied successfully')
    onAutomationCreated?.()
  }

  const createAutomation = async (values: AutomationFormData) => {
    // Transform form data to match database structure
    const automationData: AutomationCreatePayload = {
      name: values.name,
      description: values.description,
      is_active: true,
      // Create condition group with single condition
      condition_groups: [
        {
          logical_operator: 'OR',
          order_index: 0,
          conditions: [cleanAutomationConditionPayload(values, 0)],
        },
      ],
      actions: [cleanAutomationActionPayload(values)],
    }

    if (values.action_type === 'add_company_note') {
      // Create multipart payload for company note automation with attachments
      const multipartData: AutomationMultipartPayload = {
        name: values.name,
        description: values.description,
        is_active: true,
        condition_groups: automationData.condition_groups,
        actions: automationData.actions,
        attachments: values.attachments || [],
      }
      return api.createCompanyNoteAutomation(multipartData)
    }

    return api.createAutomation(automationData)
  }

  // Create a copy of the automation with (Copy) appended to the name
  const automationToCopy = automation
    ? {
        ...automation,
        name: `${automation.name} (Copy)`,
      }
    : undefined

  return (
    <AutomationModal
      show={show}
      handleClose={handleClose}
      onSubmitSuccess={handleSubmitSuccess}
      confirmButtonText="Create"
      submitApiAction={createAutomation}
      automationToEdit={automationToCopy}
      loading={isLoadingAutomation}
    />
  )
}
